import { useEffect, useState } from 'react'
import { Collapse } from '@mui/material'
import { Board, Header, MainContent } from 'src/lib'
import { useUserStore } from 'src/store/userStore'
import { useDrawerStore } from 'src/store/drawerStore'
import { useSnackbarStore } from 'src/store/snackbarStore'
import { getPresignedUrl, uploadToS3 } from 'src/services/api/dataImport'
import { FileTypeSection } from './FileTypeSection'
import { PlatformSection } from './PlatformSection'
import { UploadSection } from './UploadSection'
import { FILE_TYPE_TO_API_TYPE, FILE_TYPES } from './types'

const DataImport = () => {
    const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null)
    const [selectedFileType, setSelectedFileType] = useState<string | null>(null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [isUploading, setIsUploading] = useState(false)

    useEffect(() => {
        const { open, closeDrawer, openDrawer } = useDrawerStore.getState()
        if (open) {
            closeDrawer()
            return () => openDrawer()
        }
    }, [])

    const handlePlatformSelect = (id: string) => {
        if (selectedPlatform !== id) {
            setSelectedPlatform(id)
            setSelectedFileType(null)
            setSelectedFile(null)
        }
    }

    const handleFileTypeSelect = (id: string) => {
        if (selectedFileType !== id) {
            setSelectedFileType(id)
            setSelectedFile(null)
        }
    }

    const handleImport = async () => {
        if (!selectedFile || !selectedFileType) return

        const apiType = FILE_TYPE_TO_API_TYPE[selectedFileType]
        if (!apiType) return

        const { cognitoIdToken } = useUserStore.getState().tokens
        const showSnackbar = useSnackbarStore.getState().showSnackbar

        setIsUploading(true)
        try {
            const { url } = await getPresignedUrl(apiType, selectedFile.name, cognitoIdToken)
            await uploadToS3(url, selectedFile)
            showSnackbar('Arquivo enviado com sucesso!', 'success')
            setSelectedFile(null)
            setSelectedFileType(null)
        } catch {
            showSnackbar('Erro ao enviar arquivo. Tente novamente.', 'error')
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <MainContent>
            <Header title="Importação de Dados" />
            <Board>
                <PlatformSection
                    selectedPlatform={selectedPlatform}
                    onSelect={handlePlatformSelect}
                />

                <Collapse in={!!selectedPlatform} timeout={280}>
                    {selectedPlatform && (
                        <FileTypeSection
                            platform={selectedPlatform}
                            selectedFileType={selectedFileType}
                            onSelect={handleFileTypeSelect}
                        />
                    )}
                </Collapse>

                <Collapse in={!!selectedFileType} timeout={280}>
                    <UploadSection
                        selectedFile={selectedFile}
                        onFileSelect={setSelectedFile}
                        onClear={() => setSelectedFile(null)}
                        onImport={handleImport}
                        isLoading={isUploading}
                        fileTypeLabel={
                            selectedPlatform && selectedFileType
                                ? FILE_TYPES[selectedPlatform]?.find(ft => ft.id === selectedFileType)?.label
                                : undefined
                        }
                    />
                </Collapse>
            </Board>
        </MainContent>
    )
}

export { DataImport }